import { useMemo, useState } from "react";
import { BookOpen, Search, ExternalLink } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { useAuth } from "../context/AuthContext";
import useGlossary from "../lib/useGlossary";

/**
 * Global floating "Glossary" button that opens the glossary in a
 * side panel. Lives on the bottom-LEFT, stacked just above the Help launcher,
 * so it never overlaps the Founder Mentor launcher (bottom-right).
 */
export default function GlossaryLauncher() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  if (!user) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="glossary-launcher-btn"
        aria-label="Open glossary"
        className="fixed bottom-24 left-6 z-40 group flex items-center px-2 h-12 bg-white hover:bg-neutral-900 text-neutral-900 hover:text-white border-2 border-neutral-900 shadow-[6px_6px_0_0_rgba(0,0,0,0.18)] hover:shadow-[8px_8px_0_0_rgba(0,0,0,0.24)] transition-colors rounded-none"
        style={{ bottom: "calc(6rem + env(safe-area-inset-bottom))" }}
      >
        <span className="w-7 h-7 flex items-center justify-center bg-[#2F8F6B] text-white flex-shrink-0">
          <BookOpen className="w-4 h-4" />
        </span>
        <span className="max-w-0 opacity-0 ml-0 group-hover:max-w-[120px] group-hover:opacity-100 group-hover:ml-2 group-hover:mr-1 group-focus-visible:max-w-[120px] group-focus-visible:opacity-100 group-focus-visible:ml-2 overflow-hidden whitespace-nowrap transition-[max-width,opacity,margin] duration-300 ease-out text-xs uppercase tracking-[0.25em] font-bold">
          Glossary
        </span>
      </button>
      <GlossarySheet open={open} onOpenChange={setOpen} />
    </>
  );
}

export function GlossarySheet({ open, onOpenChange }) {
  const data = useGlossary();
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return data.terms;
    return data.terms.filter((t) =>
      (t.term + " " + (t.aliases || []).join(" ") + " " + t.definition)
        .toLowerCase()
        .includes(needle),
    );
  }, [data, q]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col border-l-2 border-neutral-900"
        data-testid="glossary-sheet"
      >
        <SheetHeader className="px-5 py-4 border-b-2 border-neutral-900">
          <SheetTitle className="font-display font-black text-2xl tracking-tighter flex items-center gap-2">
            <BookOpen className="w-5 h-5" /> Glossary
          </SheetTitle>
          <SheetDescription className="text-xs">
            {data?.terms?.length || 0} founder-friendly terms, defined in plain English.
          </SheetDescription>
        </SheetHeader>

        <div className="px-5 py-3 border-b border-neutral-200">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              type="text"
              placeholder="Search terms… (try 'burn', 'CAC')"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="glossary-launcher-search"
              className="w-full border border-neutral-300 pl-10 pr-3 py-2.5 text-sm focus:border-neutral-900 focus:outline-none rounded-none"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4" data-testid="glossary-launcher-results">
          {!data && (
            <p className="text-sm text-neutral-500 italic">Loading…</p>
          )}
          {data && filtered.length === 0 && (
            <p className="text-sm text-neutral-600 py-10 text-center" data-testid="glossary-launcher-empty">
              No terms match "<b>{q}</b>".
            </p>
          )}
          <dl className="space-y-4">
            {filtered.map((t) => (
              <div key={t.term} data-testid={`glossary-launcher-entry-${t.term.toLowerCase().replace(/\s+/g, "-")}`}>
                <dt className="font-bold text-neutral-900 text-sm">
                  {t.term}
                  {t.aliases?.length > 0 && (
                    <span className="block text-[10px] uppercase tracking-[0.12em] text-neutral-400 font-normal mt-0.5">
                      also: {t.aliases.join(", ")}
                    </span>
                  )}
                </dt>
                <dd className="text-sm text-neutral-700 leading-relaxed mt-1">
                  {t.definition}
                  {t.learn_more_url && (
                    <a
                      href={t.learn_more_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.12em] font-bold text-neutral-600 hover:text-neutral-900 no-underline"
                    >
                      Learn more <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </SheetContent>
    </Sheet>
  );
}
